import React from 'react';
import { Dialog, Button, Box, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import { amber } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { ThemeContext } from 'src/store/ContextStore';
import { ModalDateFieldProvider } from 'src/components/UI/input/ModalDateFieldContext';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const StyledTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10
}));

const StyledDialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2)
}));

const StyledDialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: 20
}));

function hexToRGB(hex, alpha) {
  // Tolerate a theme with no accent colour. This used to throw, taking the
  // whole dialog — and so the clinical record behind it — down with it. Now
  // that the unsaved-changes prompt renders on the disease, phenotype and
  // genotype editors too, a missing colour must degrade to "no tint" rather
  // than lose someone's edits. TGCLOUD-1823.
  if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) {
    return 'transparent';
  }
  var r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
  } else {
    return 'rgb(' + r + ', ' + g + ', ' + b + ')';
  }
}

const DialogTitle = (props) => {
  const t = useAutoTranslation();

  const { children, onClose, appearance, ...other } = props;
  const { theme } = React.useContext(ThemeContext);
  const headerColor = hexToRGB(theme.accent, 0.15);
  return (
    <StyledDialogTitle {...other} sx={{ backgroundColor: headerColor }}>
      <StyledTitle style={{ opacity: 1 }}>
        {appearance === 'warning' && (
          <WarningIcon fontSize="small" style={{ color: amber[500] }} />
        )}
        <Typography variant="h6" style={{ opacity: 1 }}>
          {children}
        </Typography>
      </StyledTitle>
      {onClose ? (
        <IconButton
          aria-label={t('close')}
          onClick={onClose}
          size="large"
          style={{ opacity: 1 }}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </StyledDialogTitle>
  );
};

const AcitonDialog = ({
  open,
  title,
  handleClose,
  onSubmit,
  saveText = 'Continue',
  children,
  maxWidth = 'sm',
  minHeight = 180,
  submitColor = 'primary',
  submitDisabled = false
}) => {
  const t = useAutoTranslation();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby="custom-dialog-title"
    >
      <DialogTitle id="custom-dialog-title">{title}</DialogTitle>
      <StyledDialogContent sx={{ minHeight: minHeight }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <Box
            sx={{
              flex: 1
            }}
          >
            <ModalDateFieldProvider>{children}</ModalDateFieldProvider>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'background.paper',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <Button variant="outlined" onClick={handleClose}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color={submitColor}
              disabled={submitDisabled}
              onClick={onSubmit}
            >
              {saveText}
            </Button>
          </Box>
        </Box>
      </StyledDialogContent>
    </Dialog>
  );
};

export default AcitonDialog;
